import { splitSupportErrorId } from '../utils/analytics';

/**
 * Hasura's text when it cannot parse an action's error body. It names Hasura's
 * own parsing step, not the reason the backend refused the request, so it tells
 * the user nothing — seen on a channel save the backend refused (release 29817)
 * and on the Default Messages save (`update-default-messages.ts`).
 */
const UNPARSED_ACTION_ERROR =
  'ActionWebhookErrorResponse failed, key "message" not found';

/**
 * The prefix urql's `CombinedError` puts before each GraphQL error's message.
 * `checkForErrors` falls back to that `error.message` when the error carries no
 * `extensions.internal.response.body.errors`, so the text above can reach the
 * toast as `[GraphQL] <text>`. Only this one prefix, once, is tolerated.
 */
const URQL_GRAPHQL_PREFIX = '[GraphQL] ';

/**
 * The text of an error toast raised by `showToastMessage`: its heading, then
 * the failure's own message — except when that message is ONLY the unparsed
 * action error above (bare, or behind urql's `[GraphQL] ` prefix), which is
 * dropped while its Error ID is kept, so support can still trace the failure. A
 * message that merely contains that text among other words is shown unchanged.
 */
export const buildToastErrorText = (
  heading: string,
  message: string | undefined
): string => {
  if (message) {
    // The Error ID suffix is the one `throwErrorWithId` appends; `splitSupportErrorId`
    // recognises it, and what follows the body it returns is that suffix verbatim.
    const { message: body } = splitSupportErrorId(message);
    const trimmed = body.trim();
    const unprefixed = trimmed.startsWith(URQL_GRAPHQL_PREFIX)
      ? trimmed.slice(URQL_GRAPHQL_PREFIX.length)
      : trimmed;
    if (unprefixed === UNPARSED_ACTION_ERROR) {
      const idSuffix = message.slice(body.length).trim();
      return idSuffix ? `${heading} ${idSuffix}` : heading;
    }
  }
  return `${heading}${message ? `: ${message}` : ''}`;
};
